/**
 * Service functions for payment receipts (recibos de abono en Cobros / Créditos).
 * Not used at purchase time — the invoice alone covers paid / saldo pendiente.
 */
import { supabase } from '../lib/supabase';
import type { Factura } from '../components/facturas/types';
import type { ReciboPagoData, ReciboPdfCompanyInfo } from './reciboPagoPdfHtml';
import { generateReciboNumero } from './reciboPagoPdfHtml';
import { buildReciboPagoPdfBlob, safeReciboPdfFilename } from './reciboPagoPdfBlob';
import { isSyntheticPagoId, uploadReciboPagoPdfPublicLink } from './reciboPagoPdfUpload';
import { generarUrlWhatsApp, formatPhoneNumberForWhatsApp } from './whatsapp';

export type ReciboPagoOrigenTabla = 'pagos_cobro' | 'pagos_credito' | 'pagos_factura';

export interface GenerateReceiptResult {
  reciboUrl: string;
  reciboNumero: string;
  pdfBlob: Blob;
  filename: string;
}

/** Minimal factura-like context when full Factura is not loaded. */
export type ReciboFacturaContext = Pick<Factura, 'id' | 'numero' | 'total'> & {
  cliente?: Factura['cliente'] | null;
  telefonoEntrega?: string;
  canalVenta?: Factura['canalVenta'];
};

export async function updatePagoReciboUrl(
  pagoId: string,
  reciboUrl: string,
  tabla: ReciboPagoOrigenTabla = 'pagos_cobro'
): Promise<void> {
  const { error } = await supabase.from(tabla).update({ recibo_url: reciboUrl }).eq('id', pagoId);

  if (error) {
    console.warn('[updatePagoReciboUrl]', error.message);
    // Don't throw — migration may not be applied yet; receipt URL still works for WhatsApp
  }
}

export async function generateAndUploadReceipt(
  factura: ReciboFacturaContext,
  pagoData: {
    pagoId: string;
    monto: number;
    metodoPago: string;
    saldoAnterior: number;
    saldoDespues: number;
    notas?: string;
  },
  companyInfo: ReciboPdfCompanyInfo & { empresaId: string },
  origenTabla: ReciboPagoOrigenTabla = 'pagos_cobro'
): Promise<GenerateReceiptResult> {
  const reciboNumero = generateReciboNumero(pagoData.pagoId);

  const receiptData: ReciboPagoData = {
    pagoId: pagoData.pagoId,
    monto: pagoData.monto,
    metodoPago: pagoData.metodoPago,
    fecha: new Date(),
    saldoAnterior: pagoData.saldoAnterior,
    saldoDespues: pagoData.saldoDespues,
    notas: pagoData.notas,
    numeroRecibo: reciboNumero,
  };

  // buildReciboPagoPdfBlob expects Factura; cast minimal context (only fields used in HTML)
  const facturaForPdf = {
    ...factura,
    canalVenta: factura.canalVenta || 'TIENDA',
    cliente: factura.cliente ?? null,
  } as Factura;

  const pdfBlob = await buildReciboPagoPdfBlob(facturaForPdf, receiptData, companyInfo);
  const filename = safeReciboPdfFilename(reciboNumero);

  const reciboUrl = await uploadReciboPagoPdfPublicLink(
    pdfBlob,
    companyInfo.empresaId,
    factura.id && factura.id !== 'sin-factura' ? factura.id : pagoData.pagoId,
    pagoData.pagoId,
    reciboNumero
  );

  if (reciboUrl && !isSyntheticPagoId(pagoData.pagoId)) {
    await updatePagoReciboUrl(pagoData.pagoId, reciboUrl, origenTabla);
  } else if (!reciboUrl) {
    console.warn('[generateAndUploadReceipt] upload failed; PDF still available for download');
  }

  return {
    reciboUrl: reciboUrl || '',
    reciboNumero,
    pdfBlob,
    filename,
  };
}

export function generateWhatsAppReceiptMessage(
  factura: ReciboFacturaContext,
  reciboNumero: string,
  monto: number,
  saldoDespues: number,
  reciboUrl: string,
  companyName: string
): string {
  const formatNum = (n: number) => n.toLocaleString('es-CO');
  const clienteNombre = factura.cliente?.nombre || 'Cliente';

  let mensaje = `¡Hola ${clienteNombre}! 👋\n\n`;
  mensaje += `Confirmamos tu pago registrado en *${companyName}*:\n\n`;
  mensaje += `📄 *Recibo:* ${reciboNumero}\n`;
  mensaje += `💰 *Monto recibido:* $${formatNum(monto)}\n`;
  if (factura.numero) {
    const cliente = [factura.cliente?.nombre, factura.cliente?.apellido].filter(Boolean).join(' ').trim();
    mensaje += cliente
      ? `📋 *Factura:* ${factura.numero} · ${cliente}\n`
      : `📋 *Factura:* ${factura.numero}\n`;
  }

  if (saldoDespues > 0) {
    mensaje += `\n⏳ *Saldo pendiente:* $${formatNum(saldoDespues)}\n`;
  } else {
    mensaje += `\n✅ *Tu cuenta está saldada*\n`;
  }

  if (reciboUrl) {
    mensaje += `\n📎 Descarga tu recibo aquí:\n${reciboUrl}\n`;
  }
  mensaje += `\n¡Gracias por tu preferencia! 🙏`;

  return mensaje;
}

export function openWhatsAppWithReceipt(
  phoneNumber: string,
  message: string,
  countryCode: string = '57'
): void {
  const url = generarUrlWhatsApp(phoneNumber, message, countryCode);
  if (url) {
    window.open(url, '_blank');
  }
}

export function getClientPhoneForWhatsApp(factura: ReciboFacturaContext): string | null {
  const phone = factura.cliente?.telefono || factura.telefonoEntrega;
  if (!phone || !phone.trim()) return null;
  return formatPhoneNumberForWhatsApp(phone.trim(), '57');
}

/** Load factura + client for receipt; falls back to minimal context. */
export async function loadFacturaContextForRecibo(
  empresaId: string,
  facturaId: string | null | undefined,
  fallback: {
    total: number;
    clienteId?: string | null;
    clienteNombre?: string;
    clienteTelefono?: string | null;
  }
): Promise<ReciboFacturaContext> {
  if (facturaId) {
    const { useFacturasStore } = await import('../stores/useFacturasStore');
    const f = await useFacturasStore.getState().fetchFacturaPorId(empresaId, facturaId);
    if (f) return f;
  }

  let cliente: ReciboFacturaContext['cliente'] = null;
  if (fallback.clienteId) {
    const { data } = await supabase
      .from('clientes')
      .select('id, nombre, apellido, telefono, email, numero_documento')
      .eq('id', fallback.clienteId)
      .maybeSingle();
    if (data) {
      cliente = {
        id: data.id,
        nombre: data.nombre || fallback.clienteNombre || 'Cliente',
        apellido: data.apellido || '',
        telefono: data.telefono || fallback.clienteTelefono || undefined,
        email: data.email || undefined,
        numeroDocumento: data.numero_documento || undefined,
        empresaId,
      };
    }
  }
  if (!cliente && fallback.clienteNombre) {
    cliente = {
      id: fallback.clienteId || 'unknown',
      nombre: fallback.clienteNombre,
      telefono: fallback.clienteTelefono || undefined,
      empresaId,
    };
  }

  return {
    id: facturaId || 'sin-factura',
    numero: facturaId ? '—' : 'Cobro',
    total: fallback.total,
    cliente,
  };
}
